import {
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  Badge,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import FormModal from '../../../components/FormModal';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { CouponDto, useListCoupons } from '../hooks/useListCoupons';
import { useAssignCoupon } from '../hooks/useAssignCoupon';
import { GET_USER_COUPONS_QUERY_KEY } from '../hooks/useGetUserCoupons';

interface AssignCouponToUserModalProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function AssignCouponToUserModal({
  userId,
  isOpen,
  onClose,
}: AssignCouponToUserModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { selectedOrganization } = useSelectedOrganization();
  // Without `isError`, a refused read told the operator "no active coupons" —
  // a confident claim about the org, made while they were mid-assignment.
  // See `coupon-read-error-gate.test.ts`.
  //
  // R118-1 — and `isError` was only half of it. `useListCoupons` is gated
  // `enabled: !!organizationId`, and the ONLY caller of this modal is
  // `UserCouponList`, a card on the member page that takes a `userId` prop and
  // reads no organization at all. So an operator with nothing selected — a
  // refused or empty `my-organizations` leaves the whole session that way —
  // opens this dialog onto a read that was never issued, and React Query v4
  // reports a DISABLED query as `isLoading: true` with `fetchStatus: 'idle'`.
  // The raw flag below therefore rendered a PERMANENT "Loading…" for a request
  // nobody had sent. `isInitialLoading` (`isLoading && isFetching`) is false
  // for a disabled query, which is what makes the four states separable.
  const {
    data: coupons,
    isInitialLoading: couponsLoading,
    isError: couponsError,
  } = useListCoupons(selectedOrganization?.id || '');
  const couponsReadState = classifyRead(couponsError, couponsLoading, coupons);
  const assignCoupon = useAssignCoupon();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCouponIds, setSelectedCouponIds] = useState<string[]>([]);

  // Filter coupons based on search query
  const filteredCoupons = useMemo(() => {
    if (!coupons) return [];
    if (!searchQuery.trim()) return coupons;

    const query = searchQuery.toLowerCase();
    return coupons.filter((coupon) =>
      coupon.name.toLowerCase().includes(query),
    );
  }, [coupons, searchQuery]);

  // Filter out expired coupons
  const activeCoupons = useMemo(() => {
    return filteredCoupons.filter(
      (coupon) => new Date(coupon.expiresAt) > new Date(),
    );
  }, [filteredCoupons]);

  const handleToggleCoupon = (couponId: string) => {
    setSelectedCouponIds((prev) =>
      prev.includes(couponId)
        ? prev.filter((id) => id !== couponId)
        : [...prev, couponId],
    );
  };

  const handleToggleAll = () => {
    if (selectedCouponIds.length === activeCoupons.length) {
      setSelectedCouponIds([]);
    } else {
      setSelectedCouponIds(activeCoupons.map((c) => c.id));
    }
  };

  const handleAssign = async () => {
    if (selectedCouponIds.length === 0) {
      toast({
        title: translate('coupon.no_coupons_selected'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Assign each selected coupon to the user
      await Promise.all(
        selectedCouponIds.map((couponId) =>
          assignCoupon.mutateAsync({
            couponId,
            userIds: [userId],
          }),
        ),
      );

      // Invalidate user coupons query
      queryClient.invalidateQueries({
        queryKey: GET_USER_COUPONS_QUERY_KEY(userId),
      });

      toast({
        title: translate('coupon.assigned_successfully').replace('{count}', selectedCouponIds.length.toString()),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onModalClose();
    } catch (error) {
      toast({
        title: translate('coupon.assign_error'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const onModalClose = () => {
    setSearchQuery('');
    setSelectedCouponIds([]);
    onClose();
  };

  const getDiscountBadge = (coupon: CouponDto) => {
    if (coupon.discountType === 'PERCENTAGE') {
      return (
        <Badge colorScheme="purple" ml={2}>
          -{coupon.discountValue}%
        </Badge>
      );
    }
    return (
      <Badge colorScheme="brand" ml={2}>
        -{coupon.discountValue} {(coupon.currency || '').toUpperCase()}
      </Badge>
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onModalClose}
      title={translate('coupon.assign_coupon')}
      size="xl"
      onSubmit={handleAssign}
      submitLabel={translate('coupon.assign_coupon')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={assignCoupon.isLoading}
      isSubmitDisabled={selectedCouponIds.length === 0}
    >
      <FormControl mb={4}>
        <FormLabel>{translate('coupon.search_coupons')}</FormLabel>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder={translate('coupon.search_by_name')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </FormControl>

      {couponsReadState === 'unreadable' ? (
        <InlineErrorState message={translate('common.unexpected_error')} />
      ) : couponsReadState === 'loading' ? (
        <Text color="gray.500" textAlign="center" py={8}>
          {translate('coupon.loading')}
        </Text>
      ) : couponsReadState === 'never-issued' ? (
        // One control inside a form, so a text line rather than the 40px
        // `ScopePrompt` panel — the call run 114 settled on for
        // `AddUserToGameModal` and run 116 for the Duo attach dialog.
        // Borrowed key: `src/i18n/{en,fr}.ts` are inside the open-PR fence
        // (R96-7) and this sentence already exists in BOTH catalogues.
        <Text color="gray.500" textAlign="center" py={8}>
          {translate(scopePromptI18nKey('organization') as any)}
        </Text>
      ) : activeCoupons.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={8}>
          {searchQuery
            ? translate('coupon.no_coupons_found')
            : translate('coupon.no_active_coupons')}
        </Text>
      ) : (
        <>
          <FormControl mb={4}>
            <Checkbox
              isChecked={
                selectedCouponIds.length === activeCoupons.length &&
                activeCoupons.length > 0
              }
              onChange={handleToggleAll}
            >
              <Text fontWeight="bold">
                {translate('cohort.select_all')} ({activeCoupons.length})
              </Text>
            </Checkbox>
          </FormControl>

          <Stack spacing={2} maxH="400px" overflowY="auto">
            {activeCoupons.map((coupon) => (
              <Checkbox
                key={coupon.id}
                isChecked={selectedCouponIds.includes(coupon.id)}
                onChange={() => handleToggleCoupon(coupon.id)}
              >
                <HStack spacing={2}>
                  <Text>{coupon.name}</Text>
                  {getDiscountBadge(coupon)}
                  <Text fontSize="xs" color="gray.500">
                    ({translate('coupon.expires')}{' '}
                    {new Date(coupon.expiresAt).toLocaleDateString()})
                  </Text>
                </HStack>
              </Checkbox>
            ))}
          </Stack>

          <Text mt={4} color="gray.600" fontSize="sm">
            {selectedCouponIds.length} {translate('coupon.coupons_selected')}
          </Text>
        </>
      )}
    </FormModal>
  );
}
